import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, PackagePlus, Plus, Radar, Router } from "lucide-react";

import { api, ApiError, type BoxPlacement, type Connector } from "../../api";
import { takePendingClaim } from "../../claim";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import { Select } from "../../components/Select";
import { count } from "../../words";
import {
  CustomerRecorderDialog,
  deviceLabels,
  draftFromDevice,
  type RecorderDraft,
} from "./CustomerRecorderDialog";
import { EmptyState, Note, SettingsSection } from "./shared";

const NEW_LOCATION = "__new";

function ago(iso: string | null): string {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} minutes ago`;
  if (s < 86400) {
    const h = Math.round(s / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.round(s / 86400);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function boxName(b: Connector): string {
  return b.name && b.name !== b.serial ? b.name : "IntraScene box";
}

function statusOf(b: Connector): { label: string; tone: string } {
  if (b.online) return { label: "Online", tone: "text-emerald-300" };
  if (!b.registered) return { label: "Not switched on yet", tone: "text-bf-muted" };
  return { label: `Offline · last seen ${ago(b.last_seen_at)}`, tone: "text-red-300" };
}

type Place = { store: string; location_name: string };

function placement(p: Place): BoxPlacement {
  return p.store === NEW_LOCATION
    ? { location_name: p.location_name.trim() }
    : { store: p.store };
}

type AddDraft = Place & { serial: string; claim_code: string; name: string };
type MoveDraft = Place & { box: Connector };

/** A customer's IntraScene boxes: add one, place it, and find its recorders. */
export function BoxesSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { scope, stores } = useScope();
  const boxes = useQuery({
    queryKey: ["connectors"],
    queryFn: api.connectors.list,
    refetchInterval: 10_000,
  });
  const nvrs = useQuery({ queryKey: ["nvrs"], queryFn: api.nvrs.list });
  const [adding, setAdding] = useState<AddDraft | null>(null);
  const [moving, setMoving] = useState<MoveDraft | null>(null);
  const [removing, setRemoving] = useState<Connector | null>(null);
  const [recorder, setRecorder] = useState<RecorderDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultStore = scope.kind === "store" ? scope.store : stores[0]?.name ?? NEW_LOCATION;
  const startAdding = (serial = "", claim_code = "") => {
    add.reset();
    setAdding({ serial, claim_code, name: "", store: defaultStore, location_name: "" });
  };

  useEffect(() => {
    const pending = takePendingClaim();
    if (pending && canEdit) startAdding(pending.serial, pending.claim_code);
    // Only once, when the section opens from the QR code on a box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["connectors"] });
    qc.invalidateQueries({ queryKey: ["stores"] });
  };
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const add = useMutation({
    mutationFn: (d: AddDraft) =>
      api.connectors.claim({
        serial: d.serial.trim().toUpperCase(),
        claim_code: d.claim_code.trim().toUpperCase(),
        name: d.name.trim(),
        ...placement(d),
      }),
    onSuccess: () => {
      setAdding(null);
      refresh();
    },
    onError: fail("Could not add the box."),
  });
  const move = useMutation({
    mutationFn: (d: MoveDraft) => api.connectors.assign(d.box.id, placement(d)),
    onSuccess: () => {
      setMoving(null);
      refresh();
    },
    onError: fail("Could not move the box."),
  });
  const remove = useMutation({
    mutationFn: (b: Connector) => api.connectors.release(b.id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (e: unknown) => {
      setRemoving(null);
      fail("Could not take the box out of its location.")(e);
    },
  });
  const look = useMutation({
    mutationFn: (b: Connector) => api.connectors.discover(b.id),
    onSuccess: refresh,
    onError: fail("Could not ask the box to look for video recorders."),
  });

  const storeLabel = (name: string) =>
    stores.find((s) => s.name === name)?.display_name || name;
  const all = (boxes.data ?? []).filter(
    (b) => scope.kind !== "store" || !b.store || b.store === scope.store,
  );
  const added = (b: Connector, ip: string) =>
    (nvrs.data ?? []).some((n) => n.connector_id === b.id && n.host === ip);

  return (
    <SettingsSection
      title="IntraScene boxes"
      description="The box at each location that connects its video recorders to IntraScene."
      hint={
        <>
          Plug the box into the same network as your video recorder and switch
          it on. Then add it here with the serial and code printed on its
          label, or scan the QR code on the label with your phone.
        </>
      }
      actions={
        canEdit && (
          <button className="bf-button" onClick={() => startAdding()}>
            <PackagePlus size={16} className="mr-2" /> Add your box
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {boxes.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading boxes…</div>
      ) : all.length === 0 ? (
        <EmptyState
          icon={Router}
          title="No IntraScene box yet"
          hint="Your box comes with a label showing its serial and a code. Add it here to start seeing your cameras."
          action={
            canEdit && (
              <button className="bf-button" onClick={() => startAdding()}>
                <PackagePlus size={16} className="mr-2" /> Add your box
              </button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {all.map((b) => {
            const st = statusOf(b);
            const devices = (b.discovered?.devices ?? []).filter((d) => d.rtsp_port);
            const labels = deviceLabels(devices);
            return (
              <article key={b.id} className="bf-card space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-white">
                      {boxName(b)}
                    </h3>
                    <p className="font-mono text-xs text-bf-muted">Serial {b.serial}</p>
                  </div>
                  <span className={`text-xs ${st.tone}`}>{st.label}</span>
                </div>

                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-bf-muted">
                  <span className="flex items-center gap-1">
                    <MapPin size={13} />
                    {b.store ? storeLabel(b.store) : "Not at a location"}
                  </span>
                  {b.store && (
                    <span>
                      {count(b.recorder_ids.length, "video recorder", "video recorders")} connected
                    </span>
                  )}
                </p>

                {b.store && b.discovered && (
                  <div className="space-y-1 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                    <p className="text-xs text-bf-muted">
                      {b.discover_requested
                        ? "Looking for video recorders…"
                        : `Found on this network ${ago(new Date(b.discovered.scanned_at * 1000).toISOString())}`}
                    </p>
                    {devices.length === 0 ? (
                      <p className="text-sm text-bf-muted">
                        No video recorders answered. Check that the recorder is
                        switched on and plugged into the same network as the box.
                      </p>
                    ) : (
                      <ul className="divide-y divide-white/5">
                        {devices.map((d) => (
                          <li key={d.ip} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                            <span className="truncate text-bf-text">{labels.get(d.ip)}</span>
                            {added(b, d.ip) ? (
                              <span className="text-xs text-bf-muted">Added</span>
                            ) : (
                              canEdit && (
                                <button
                                  className="shrink-0 text-xs text-bf-muted hover:text-bf-text"
                                  onClick={() => setRecorder(draftFromDevice(b, d))}
                                >
                                  <Plus size={13} className="mr-1 inline" />
                                  Add this recorder
                                </button>
                              )
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {canEdit && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/5 pt-3 text-xs">
                    {b.store && (
                      <button
                        className="flex items-center gap-1.5 text-bf-muted hover:text-bf-text disabled:opacity-40"
                        disabled={!b.online || b.discover_requested || look.isPending}
                        title={b.online ? undefined : "The box needs to be online"}
                        onClick={() => look.mutate(b)}
                      >
                        <Radar size={14} /> Look for video recorders
                      </button>
                    )}
                    {b.store ? (
                      <button
                        className="text-bf-muted hover:text-red-400"
                        onClick={() => setRemoving(b)}
                      >
                        Take out of this location
                      </button>
                    ) : (
                      <button
                        className="text-bf-muted hover:text-bf-text"
                        onClick={() =>
                          setMoving({ box: b, store: defaultStore, location_name: "" })
                        }
                      >
                        Put at a location
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {adding && (
        <Modal onClose={() => setAdding(null)} labelledBy="box-add-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(adding);
            }}
          >
            <h3 id="box-add-title" className="text-lg font-medium text-white">
              Add your box
            </h3>
            <p className="text-sm text-bf-muted">
              The serial and code are printed on the label on the box.
            </p>
            {add.isError && <Note tone="error">{error}</Note>}
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Serial</span>
                <input
                  className="bf-input font-mono text-xs uppercase"
                  value={adding.serial}
                  onChange={(e) => setAdding({ ...adding, serial: e.target.value })}
                  placeholder="ISC-2610-0001"
                  required
                  autoFocus={!adding.serial}
                />
              </label>
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Code</span>
                <input
                  className="bf-input font-mono text-xs uppercase"
                  value={adding.claim_code}
                  onChange={(e) => setAdding({ ...adding, claim_code: e.target.value })}
                  placeholder="ABCD-EFGH"
                  autoComplete="off"
                  required
                />
              </label>
            </div>
            <LocationChoice value={adding} onChange={(p) => setAdding({ ...adding, ...p })} />
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Name for the box (optional)</span>
              <input
                className="bf-input"
                value={adding.name}
                onChange={(e) => setAdding({ ...adding, name: e.target.value })}
                placeholder="Back office"
              />
            </label>
            <DialogButtons
              onCancel={() => setAdding(null)}
              pending={add.isPending}
              disabled={!placementReady(adding)}
              label="Add box"
            />
          </form>
        </Modal>
      )}

      {moving && (
        <Modal onClose={() => setMoving(null)} labelledBy="box-move-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              move.mutate(moving);
            }}
          >
            <h3 id="box-move-title" className="text-lg font-medium text-white">
              Put {boxName(moving.box)} at a location
            </h3>
            <LocationChoice value={moving} onChange={(p) => setMoving({ ...moving, ...p })} />
            <DialogButtons
              onCancel={() => setMoving(null)}
              pending={move.isPending}
              disabled={!placementReady(moving)}
              label="Put it there"
            />
          </form>
        </Modal>
      )}

      {removing && (
        <Modal onClose={() => setRemoving(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Take {boxName(removing)} out of {storeLabel(removing.store)}?
            </h3>
            <p className="text-sm text-bf-muted">
              {removing.recorder_ids.length > 0
                ? `${count(removing.recorder_ids.length, "video recorder is", "video recorders are")} connected through this box. Remove them in Video recorders first.`
                : "The box stays in your account, ready to put at another location."}
            </p>
            <div className="flex justify-end gap-2">
              <button className="bf-button bg-white/10 bg-none" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending || removing.recorder_ids.length > 0}
                onClick={() => remove.mutate(removing)}
              >
                {remove.isPending ? "Saving…" : "Take it out"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {recorder && <CustomerRecorderDialog draft={recorder} onClose={() => setRecorder(null)} />}
    </SettingsSection>
  );
}

function placementReady(p: Place): boolean {
  return p.store === NEW_LOCATION ? Boolean(p.location_name.trim()) : Boolean(p.store);
}

function LocationChoice({
  value,
  onChange,
}: {
  value: Place;
  onChange: (p: Place) => void;
}) {
  const { stores } = useScope();
  return (
    <div className="space-y-2">
      <div>
        <span className="mb-1 block text-xs text-bf-muted">Location</span>
        <Select
          ariaLabel="Location"
          value={value.store}
          onChange={(store) => onChange({ ...value, store })}
          options={[
            ...stores.map((s) => ({ value: s.name, label: s.display_name || s.name })),
            { value: NEW_LOCATION, label: "A new location" },
          ]}
        />
      </div>
      {value.store === NEW_LOCATION && (
        <label className="block">
          <span className="mb-1 block text-xs text-bf-muted">Name of the new location</span>
          <input
            className="bf-input"
            value={value.location_name}
            onChange={(e) => onChange({ ...value, location_name: e.target.value })}
            placeholder="Main Street shop"
            required
          />
        </label>
      )}
    </div>
  );
}

function DialogButtons({
  onCancel,
  pending,
  disabled,
  label,
}: {
  onCancel: () => void;
  pending: boolean;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" className="bf-button bg-white/10 bg-none" onClick={onCancel}>
        Cancel
      </button>
      <button className="bf-button disabled:opacity-40" type="submit" disabled={pending || disabled}>
        {pending ? "Saving…" : label}
      </button>
    </div>
  );
}
